/**
 * English translations for Ключ-Астром MCP Chat Client
 */

import { Translation } from './ru';

export const en: Translation = {
  // Common
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    dismiss: 'Dismiss',
    search: 'Search',
    all: 'All',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    add: 'Add',
    remove: 'Remove',
    update: 'Update',
    refresh: 'Refresh',
    clear: 'Clear',
    export: 'Export',
    import: 'Import',
    settings: 'Settings',
    help: 'Help',
    about: 'About',
    page: 'Page',
    of: 'of',
    items: { one: 'item', other: 'items' },
    previous: 'Previous',
    or: 'or',
    accept: 'Accept',
    reject: 'Dismiss',
  },

  // Navigation
  nav: {
    chat: 'Chat',
    settings: 'Settings',
    analytics: 'Analytics',
    tools: 'Tools',
    servers: 'Servers',
  },

  // Chat Interface
  chat: {
    title: 'Ключ-Астром MCP Chat',
    newChat: 'New Chat',
    placeholder: 'Type a message...',
    send: 'Send',
    thinking: 'Thinking...',
    generating: 'Generating response...',
    noMessages: 'No messages yet. Start a conversation!',
    copyCode: 'Copy code',
    copied: 'Copied!',
    // STORY-520: AUTO mode answered from another model (not part of the answer text)
    failoverNote: '{failed} did not answer (provider-side error); {model} answered.',
    // STORY-520: AUTO moved off a model but no model answered (all failed, or the next one refused)
    failoverNoteNoAnswer: '{failed} did not answer (provider-side error).',
    regenerate: 'Regenerate',
    stop: 'Stop',
    /** STORY-299: screen-reader text and title suffix of a tab whose chat is waiting on an answer. */
    tabPending: 'answering…',
    clearHistory: 'Clear history',
    clearHistoryConfirm: 'Are you sure you want to clear all chat history?',
    deleteMessage: 'Delete message',
    deleteMessageConfirm: 'Are you sure you want to delete this message?',
    sessionTitle: 'Session title',
    renameSession: 'Rename session',
    deleteSession: 'Delete session',
    deleteSessionConfirm: 'Are you sure you want to delete this session?',
    pinSession: 'Pin session',
    unpinSession: 'Unpin session',
    exportSession: 'Export session',
    importSession: 'Import session',
    // message actions, tool details, history (F-7)
    copyMessage: 'Copy message',
    copiedToClipboard: 'Copied to clipboard',
    copyFailed: 'Failed to copy',
    copy: 'Copy',
    copyJson: 'Copy JSON',
    copyOutput: 'Copy output',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    arguments: 'Arguments',
    output: 'Output',
    calledTimes: 'Called {count} with identical parameters',
    tokens: 'Tokens',
    jumpToLatest: 'Jump to latest',
    toolTimeline: 'Tool timeline',
    noData: 'No data.',
    noMatchingRows: 'No matching rows on this page.',
    problemsLoaded: {
      one: 'Showing {loaded} problem of {total}',
      other: 'Showing {loaded} problems of {total}',
    },
    itemsLoaded: 'Showing {loaded} of {total}',
    loadingPage: 'Loading page {page}…',
    pageFetchFailed: 'Failed to fetch page',
    collectMore: 'Collect More',
    collectMoreRemaining: 'Collect More ({remaining} remaining)',
    collecting: 'Collecting…',
    collectMoreFailed: 'Failed to collect more data',
    loadingHistory: 'Loading history…',
  },

  // Settings
  settings: {
    title: 'Settings',
    appearance: 'Appearance',
    language: 'Language',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    fontSize: 'Font size',
    fontSizeSmall: 'Small',
    fontSizeMedium: 'Medium',
    fontSizeLarge: 'Large',
    codeTheme: 'Code theme',
    autoSave: 'Auto save',
    showTimestamps: 'Show timestamps',
    enableNotifications: 'Enable notifications',
    
    // LLM Provider Settings
    llmProvider: 'LLM Provider',
    llmProviders: 'LLM Providers',
    addProvider: 'Add provider',
    editProvider: 'Edit provider',
    deleteProvider: 'Delete provider',
    providerName: 'Provider name',
    providerType: 'Provider type',
    providerModel: 'Model',
    providerEnabled: 'Enabled',
    apiKey: 'API key',
    apiUrl: 'API URL',
    maxTokens: 'Max tokens',
    temperature: 'Temperature',
    temperaturePrecise: 'Precise (0)',
    temperatureBalanced: 'Balanced (0.7)',
    temperatureCreative: 'Creative (2)',
    topP: 'Top P',
    presencePenalty: 'Presence penalty',
    frequencyPenalty: 'Frequency penalty',
    llmMode: 'LLM Mode',
    llmModeAuto: 'Auto (Parallel)',
    llmModeAutoDescription: 'Run all available providers in parallel and merge results',
    llmModeSingle: 'Single Provider',
    llmModeSingleDescription: 'Use only the selected provider',
    selectProvider: 'Select Provider',
    selectedProviderInfo: 'Selected provider will be used for all chat requests',
    initializingProvider: 'Initializing provider...',
    providerInitialized: 'Provider initialized successfully',
    providerInitializationFailed: 'Failed to initialize provider',
    testingProvider: 'Testing provider...',
    providerTestSuccess: 'Provider test successful',
    providerTestFailed: 'Provider test failed',
    providerNotPersisted: 'Provider is active but could not be saved to configuration — it will be lost on restart. Check backend file permissions.',
    
    // MCP Server Settings
    mcpServers: 'MCP Servers',
    addServer: 'Add server',
    editServer: 'Edit server',
    deleteServer: 'Delete server',
    serverName: 'Server name',
    serverUrl: 'Server URL',
    serverTransport: 'Transport',
    serverEnabled: 'Enabled',
    serverStatus: 'Status',
    serverConnected: 'Connected',
    serverDisconnected: 'Disconnected',
    serverConnecting: 'Connecting...',
    serverError: 'Error',
    testConnection: 'Test connection',
    refreshServers: 'Refresh servers',
    
    // Import/Export
    importExport: 'Import/Export',
    exportSettings: 'Export settings',
    importSettings: 'Import settings',
    exportAllSessions: 'Export all sessions',
    importSessions: 'Import sessions',
    exportCurrentSession: 'Export current session',
    completeBackup: 'Complete Backup',
    chatSessions: 'Chat Sessions',
    settingsOnly: 'Settings Only',
    exportAllDataDescription: 'Export all data including sessions, settings, and server configurations',
    exportSessionsDescription: 'Export only your chat history and conversations',
    exportSettingsDescription: 'Export MCP servers, LLM providers, and app preferences',
    importData: 'Import Data',
    chooseFileToImport: 'Choose File to Import',
    importDescription: 'Select a JSON file exported from this application. The import will merge with your existing data.',
    importTooLarge: 'This backup file is too large: {size} MB. The maximum size is {limit} MB. Nothing was imported.',
    importTooManySessions: 'This backup contains {actual} chats. At most {allowed} can be imported at once. Nothing was imported.',
    importTooManyMessages: 'This backup contains {actual} messages. At most {allowed} can be imported at once. Nothing was imported.',
    importProgress: 'Importing chats: {done} of {total}…',
    importPartialFailure: 'Imported {imported} of {total} chats, then the import stopped: {error}. The file is fine — this was a server or connection failure. The chats already imported have been kept; importing the same file again will duplicate them.',
    importSettingsSkipped: 'These settings in the file are unknown or have an invalid value and were not applied: {keys}.',
    currentDataSummary: 'Current Data Summary',
    chatSessionsLabel: 'Chat Sessions:',
    mcpServersLabel: 'MCP Servers:',
    llmProvidersLabel: 'LLM Providers:',
    totalMessagesLabel: 'Total Messages:',
    dangerZone: 'Danger Zone',
    clearAllData: 'Clear All Data',
    clearAllDataConfirm: 'Are you sure you want to clear all data? This action cannot be undone.',
    clearAllDataWarning: 'This will permanently delete all your chat sessions, server configurations, and settings. This action cannot be undone.',
    clearAllDataPartial: 'Deleted {deleted} of {total} chats. {failed} could not be deleted on the server — they will reappear after the page reloads.',
    clearAllDataSettingsNotReset: 'Settings could not be reset on the server.',
    importExportDescription: 'Backup and restore your chat data, settings, and configurations',
    
    // LLM Provider additional keys
    providerTypeGigachat: 'GigaChat',
    providerTypeOpenai: 'OpenAI',
    providerTypeAnthropic: 'Anthropic',
    providerTypeOllama: 'Ollama',
    providerTypeCustom: 'Custom',
    endpointUrl: 'Endpoint URL',
    modelParameters: 'Model Parameters',
    active: 'Active',
    activate: 'Activate',
    // STORY-520: provider health from real calls
    providerFailing: 'Failing',
    providerFailingTitle: 'Calls to this provider are failing ({kind}, {count} in a row, since {since}). AUTO mode asks the other providers first for a few minutes; one successful answer clears this.',
    providerErrorKindProviderError: 'provider-side error',
    providerErrorKindTimeout: 'no answer in time',
    addNewProvider: 'Add New Provider',
    noProvidersConfigured: 'No LLM providers configured',
    addProviderToEnable: 'Add a provider to enable chat functionality',
    configureLanguageModels: 'Configure language model providers for chat functionality',
    providerPlaceholder: 'My LLM Provider',
    apiKeyPlaceholder: 'Your API key',
    endpointPlaceholder: 'https://api.example.com/v1',
    modelPlaceholder: 'e.g. gpt-4o, claude-sonnet-4-20250514, GigaChat-Max',

    // Provider type selection
    selectProviderType: 'Select provider type...',

    // GigaChat advanced settings
    gigachatBaseUrl: 'API Base URL',
    gigachatAuthUrl: 'OAuth URL',
    gigachatAdvanced: 'GigaChat Advanced',
    gigachatCertFile: 'Client Certificate (PEM path)',
    gigachatKeyFile: 'Client Key (PEM path)',
    gigachatCaBundle: 'CA Bundle (PEM path)',
    gigachatCertHint: 'For mTLS auth. Leave empty to use credentials-based auth. Inline PEM can be stored in Vault.',

    // OpenAI advanced settings
    providerOpenaiAdvanced: 'OpenAI Advanced',
    providerOpenaiAdvancedHint: 'The endpoint URL above can be changed to use OpenAI-compatible APIs (e.g., Azure OpenAI, local proxies).',

    // Anthropic advanced settings
    providerAnthropicAdvanced: 'Anthropic Advanced',
    providerAnthropicAdvancedHint: 'The endpoint URL above can be changed if using an Anthropic-compatible proxy.',

    // Cluster management
    clusters: 'Clusters',
    clustersDescription: 'Manage Astromkey cluster connections',
    addCluster: 'Add Cluster',
    addNewCluster: 'Add New Cluster',
    clusterName: 'Cluster Name',
    clusterUrl: 'Cluster URL',
    noClusters: 'No clusters configured',
    clusterBoundNotice: 'Multi-cluster disabled — license is cluster-bound',
    verifySSL: 'Verify SSL certificate',
    verifySSLHint: 'Disable for self-signed certificates',
    caBundle: 'CA Certificate (PEM)',
    uploadCaBundle: 'Upload PEM',
    caBundleUploaded: 'CA bundle uploaded',
    caBundleHint: 'For clusters with internal CA certificates',
    queryRouting: 'Query Routing',
    routingSequential: 'Sequential',
    routingParallel: 'Parallel',
    clusterBase: 'base',
    sslOff: 'SSL off',
    clusterTest: 'Test',
    inactive: 'Inactive',
    healthApiReachable: 'API reachable',
    healthAuthFailed: 'Authentication failed (check tokens)',
    healthConnectionRefused: 'Connection refused',
    healthTimedOut: 'Connection timed out',
    healthHealthy: 'healthy',
    healthDegraded: 'degraded',
    healthUnreachable: 'unreachable',
    healthUnknown: 'unknown',
    healthStatusOk: 'ok',
    healthStatusReady: 'ready',
    healthStatusError: 'error',
    healthStatusDegraded: 'degraded',
    healthStatusUnconfigured: 'unconfigured',
    healthStatusInitializing: 'initializing',
    mcpClientConnected: 'connected',
    mcpClientUnavailable: 'not available',

    // MCP Server additional keys
    transportSse: 'SSE (Server-Sent Events)',
    transportHttp: 'HTTP',
    transportWebsocket: 'WebSocket',
    authenticationToken: 'Authentication Token (Optional)',
    authenticationTokenPlaceholder: 'Bearer token or API key',
    noServersConfigured: 'No MCP servers configured',
    addServerToEnable: 'Add a server to enable external tool integration',
    configureMCPDescription: 'Configure Model Control Protocol servers for external tool integration',
    serverPlaceholder: 'My MCP Server',
    serverUrlPlaceholder: 'http://localhost:9001',
    backendStatus: 'Backend',
    mcpStatus: 'MCP',
    checkedAt: 'Checked:',
    
    // Appearance additional keys
    chatPreferences: 'Chat Preferences',
    showMessageTimestamps: 'Show message timestamps',
    autoSaveChatSessions: 'Auto-save chat sessions',
    enableDesktopNotifications: 'Enable desktop notifications',
    codeThemeVsDark: 'VS Code Dark',
    codeThemeGithub: 'GitHub Light',
    codeThemeMonokai: 'Monokai',
    codeThemeDracula: 'Dracula',
    languageAffectsLLM: 'Language changes also affect LLM responses',
    customizeLookAndFeel: 'Customize the look and feel of your chat interface',
    
    // Vault Settings
    vault: 'Vault Secrets',
    vaultDescription: 'Manage secrets and certificates for MCP services',
    vaultStatus: 'Vault Status',
    vaultConnected: 'Connected',
    secretsManagement: 'Secrets',
    llmProvidersHint: 'LLM provider credentials (GigaChat, OpenAI, Anthropic, Ollama) are managed in the LLM Providers tab. This section covers infrastructure and service secrets only.',
    reloadAllServices: 'Reload All Services',
    updateSecret: 'Update',
    
    // Secret categories
    mcpSecrets: 'MCP Configuration',
    chatSecrets: 'Chat Configuration',
    ollamaSecrets: 'Ollama Configuration',
    emailSecrets: 'Email & Notifications',
    telegramSecrets: 'Telegram Bot',

    // Secret labels
    clusterBaseUrl: 'Cluster Base URL',
    clusterApiToken: 'Cluster API Token',
    environmentApiToken: 'Environment API Token',
    gigachatCredentials: 'GigaChat Credentials',
    gigachatModel: 'GigaChat Model',
    gigachatScope: 'GigaChat Scope',
    vaultGigachatBaseUrl: 'GigaChat API Base URL',
    vaultGigachatAuthUrl: 'GigaChat OAuth URL',
    vaultGigachatVerifySsl: 'GigaChat Verify SSL',
    vaultGigachatCertFile: 'GigaChat Client Certificate (file path)',
    vaultGigachatKeyFile: 'GigaChat Client Key (file path)',
    vaultGigachatCaBundleFile: 'GigaChat CA Bundle (file path)',
    vaultGigachatKeyFilePassword: 'GigaChat Key Password',
    vaultGigachatCertPem: 'GigaChat Client Certificate (PEM inline)',
    vaultGigachatKeyPem: 'GigaChat Client Key (PEM inline)',
    vaultGigachatCaBundlePem: 'GigaChat CA Bundle (PEM inline)',
    mcpServerUrl: 'MCP Server URL',
    ollamaEnabled: 'Ollama Enabled',
    ollamaBaseUrl: 'Ollama URL',
    ollamaModels: 'Ollama Models',
    ollamaDefaultModel: 'Default Model',
    // Email / SMTP
    smtpVerifyEmail: 'Email Verification',
    smtpHost: 'SMTP Host',
    smtpPort: 'SMTP Port',
    smtpUser: 'SMTP Username',
    smtpPassword: 'SMTP Password',
    smtpFrom: 'From Address',
    smtpUseTls: 'Use TLS',
    appBaseUrl: 'App Base URL',
    // Telegram
    telegramBotToken: 'Bot Token',
    
    // Certificates
    certificates: 'Certificates',
    certificateStatus: 'Certificate Status',
    certificateValid: 'Valid',
    certificateExpired: 'Expired',
    certificateExpiry: 'Expires',
    daysRemaining: 'days remaining',
    certificateSource: 'Source',
    noCertificate: 'No certificate',
    uploadCertificate: 'Upload Certificate',
    storeInVault: 'Store in Vault ',
    vaultStorageHint: 'Certificate will be stored in Vault KV',
    fileStorageHint: 'Certificate will be saved to filesystem',

    // Extension Certificates
    extensionCertificates: 'Extension Certificates',
    extensionCertDescription: 'Manage extension certificates that extend API limits or validity periods',
    noExtensionCerts: 'No extension certificates installed',
    uploadExtensionCert: 'Upload Extension Certificate',
    revokeExtensionCert: 'Revoke',
    extensionType: 'Type',
    apiLimit: 'API Limit',
    extensionDays: 'Extension Days',
    certValidFrom: 'Valid From',
    certValidUntil: 'Valid Until',
    certRevoked: 'Revoked',
    certificateInMcp: 'MCP server',
    certOtherInMcp: 'Runs a different certificate',
    certActive: 'In force',
    certInactive: 'Not in force',
    certStatusUnknown: 'MCP status unavailable',
    certError: 'Error',
    confirmRevoke: 'Are you sure you want to revoke this extension certificate? This cannot be undone.',

    // Vault warnings
    vaultNotEnabled: 'Vault Not Enabled',
    vaultNotEnabledHint: 'Secrets are loaded from environment variables. To enable Vault, set VAULT_ENABLED=true and configure VAULT_ADDR and VAULT_TOKEN.',
    vaultNotConfigured: 'Not configured',
    vaultDisconnected: 'Disconnected',
    vaultDisconnectedHint: 'Vault is enabled but could not be reached. Check Vault status and restart the service to reconnect.',
    vaultNotConfiguredHint: 'Set VAULT_ADDR and VAULT_TOKEN, then restart the service.',
    vaultAuthFailed: 'Authentication Failed',
    vaultAuthFailedHint: 'Vault is reachable but rejected the token. Update VAULT_TOKEN and restart the service.',
    vaultErrorClientNotInitialized: 'Vault client not initialized (set VAULT_ADDR and VAULT_TOKEN)',
    vaultErrorAuthFailed: 'Vault rejected the token',
    vaultErrorCouldNotConnect: 'Could not connect to Vault',
    vaultErrorDisabled: 'Vault is disabled',

    // Secret field status
    secretSource: 'Source',
    secretSourceEnvironment: 'environment',
    secretSourceVault: 'Vault',
    secretSourceUnknown: 'unknown',
    secretNotConfigured: 'Not configured',
    secretEnabled: 'Enabled',
    secretDisabled: 'Disabled',
    clickToEnable: 'Click to enable',
    clickToDisable: 'Click to disable',
    maxApiCalls: 'Max API calls',
    certificateSourceFile: 'file',

    // Environment file security
    envFileSecurity: 'Environment File Security',
    envFileSecurityHint: 'Once all secrets are stored in Vault, you can remove sensitive values from the .env file.',
    addToVault: 'Add to Vault',
    setAllSecretsHint: 'Set all secrets above before clearing the .env file',
    clearEnvConfirm: 'This will remove all secret values from the .env file. Secrets will only be available from Vault. This action cannot be undone easily. Continue?',
    confirmClear: 'Yes, Clear Secrets',
    clearEnvSecrets: 'Clear .env Secrets',
    secretMigrated: "Secret '{key}' migrated to Vault.",
    envSecretsCleared: 'Cleared {count} secret(s) from .env. Values are now managed exclusively by Vault.',
    envSecretsClearedMcpKept: "Cleared {count} secret(s) from the chat service's .env; the chat service now reads them from Vault. The MCP server's .env was left as is — see why below.",
    envClearMcpVaultOff: "Left {path} unchanged: the MCP server's own Vault is not enabled, so it reads the cluster tokens from this file.",
    envClearMcpUnreachable: 'Left {path} unchanged: the MCP server could not be reached to confirm that it reads these secrets from Vault.',
    envClearFileNotFound: 'No MCP server .env at {path} — nothing to clear there.',
    envClearFileFailed: 'Could not rewrite {path} — its secrets are still in it.',
    envClearBackupsLeft: 'Could not clear the backup copies {names} — delete them by hand; they may still hold the secrets.',
    envClearLegacyKept: 'Left unchanged (the placeholder of an earlier purge, and no value in Vault): {keys}. Migrate them to Vault, then clear again.',
    
    // Token Usage tab
    tokenUsage: 'Token Usage',

    // Toast messages
    secretSaved: 'Secret saved',
    reloadSuccess: 'Services reloaded',
    certificateUploaded: 'Certificate uploaded',

    // LLM provider management
    providerDeactivationFailed: 'Deactivation failed',
    secretAlreadySet: 'Key is already configured. Only enter a value to change it.',

    // Shared settings
    settingsApplied: 'Shared settings applied',
    settingsRejected: 'Shared settings dismissed',
    sharedWithYou: 'Settings shared with you',
    settingsShared: 'Settings shared successfully',
    shareSettings: 'Share settings',
    selectSettings: 'Select settings to share',
    selectAdmins: 'Share with',
    share: 'Share',
  },

  // STORY-289: counted nouns of the settings screens. Not inside `settings`:
  // VaultSettings indexes that section by key and needs plain strings.
  settingsCounts: {
    toolsAvailable: { one: 'tool available', other: 'tools available' },
    daysRemaining: { one: 'day remaining', other: 'days remaining' },
  },

  // Tools
  tools: {
    title: 'MCP Tools',
    availableTools: 'Available tools',
    noTools: 'No tools available',
    toolName: 'Tool name',
    toolDescription: 'Tool description',
    toolParameters: 'Parameters',
    executeTool: 'Execute tool',
    executing: 'Executing...',
    executed: 'Executed',
    executionFailed: 'Execution failed',
    result: 'Result',
    error: 'Error',
    noParameters: 'No parameters',
    requiredParameter: 'Required parameter',
    optionalParameter: 'Optional parameter',
    parameterType: 'Type',
    parameterDefault: 'Default',
    favoriteTools: 'Favorite tools',
    addToFavorites: 'Add to favorites',
    removeFromFavorites: 'Remove from favorites',
  },

  // Timeline
  timeline: {
    title: 'Tool timeline',
    toolInvoked: 'Tool invoked',
    duration: 'Duration',
    status: 'Status',
    arguments: 'Arguments',
    noEvents: 'No events in timeline',
    clearTimeline: 'Clear timeline',
  },

  // Errors
  errors: {
    generic: 'An error occurred',
    network: 'Network error',
    timeout: 'Request timed out',
    unauthorized: 'Unauthorized',
    forbidden: 'Access forbidden',
    notFound: 'Not found',
    serverError: 'Server error',
    invalidInput: 'Invalid input',
    missingParameter: 'Missing required parameter',
    connectionFailed: 'Connection failed',
    executionFailed: 'Execution failed',
    messageTooLong: 'The message is too long: {length} characters, and at most {max} are accepted. Send a shorter one — for a log, the part that matters.',
  },

  // Success messages
  success: {
    saved: 'Saved successfully',
    deleted: 'Deleted successfully',
    updated: 'Updated successfully',
    connected: 'Connected successfully',
    disconnected: 'Disconnected successfully',
    exported: 'Exported successfully',
    imported: 'Imported successfully',
    copied: 'Copied to clipboard',
  },

  // Authentication
  auth: {
    loginTitle: 'Sign In',
    registerTitle: 'Create Account',
    username: 'Username',
    usernamePlaceholder: 'Enter your username',
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    password: 'Password',
    loginButton: 'Sign In',
    registerButton: 'Create Account',
    logout: 'Sign Out',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    registerLink: 'Create one',
    loginLink: 'Sign in',
    loginFailed: 'Invalid email or password',
    registerFailed: 'Registration failed. Please try again.',
    emailNotVerifiedError: 'Your email has not been verified yet. Please check your inbox or resend the verification link.',
    // Email verification
    checkYourEmail: 'Check your email',
    verificationSent: 'We sent a verification link to:',
    verificationHint: 'Click the link in the email to verify your address. Check your spam folder if you don\'t see it.',
    resendVerification: 'Resend verification email',
    resendFailed: 'Failed to resend verification email',
    verifyingEmail: 'Verifying your email...',
    emailVerified: 'Email verified!',
    emailVerifiedHint: 'Your email has been confirmed. You can now sign in.',
    verificationFailed: 'Verification failed',
    accountCreated: 'Account created!',
    accountCreatedHint: 'Your account has been created. You can now sign in.',
    goToLogin: 'Go to Sign In',
    // Forgot / Reset password
    forgotPassword: 'Forgot password?',
    forgotPasswordTitle: 'Reset Password',
    forgotPasswordHint: 'Enter the email address associated with your account and we\'ll send you a link to reset your password.',
    forgotPasswordFailed: 'Failed to send reset email',
    sendResetLink: 'Send Reset Link',
    resetPasswordTitle: 'Set New Password',
    resetPasswordHint: 'Enter your new password below.',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    passwordMismatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 6 characters',
    resetPasswordButton: 'Reset Password',
    resetPasswordFailed: 'Password reset failed. The link may have expired.',
    passwordResetSuccess: 'Password reset!',
    passwordResetSuccessHint: 'Your password has been changed. You can now sign in with your new password.',
    ssoLogin: 'Sign in with SSO',
    ssoFailed: 'SSO sign-in failed. Please try again.',
    ssoAccountDisabled: 'SSO sign-in refused: this account is deactivated.',
    ssoEmailNotVerified: 'SSO sign-in refused: the identity provider has not verified this e-mail address.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
  },

  // Admin
  admin: {
    userManagement: 'User Management',
    totalUsers: { one: 'user', other: 'users' },
    searchPlaceholder: 'Search by username or email…',
    role: 'Role',
    emailStatus: 'Email',
    status: 'Status',
    lastLogin: 'Last Login',
    actions: 'Actions',
    active: 'Active',
    deactivated: 'Deactivated',
    emailVerified: 'Verified',
    emailNotVerified: 'Not verified',
    deactivate: 'Deactivate',
    activate: 'Activate',
    deactivateUser: 'Deactivate user',
    activateUser: 'Activate user',
    page: 'Page',
    noUsers: 'No users found',
    loadFailed: 'Failed to load users',
    actionFailed: 'Action failed',
    changeRole: 'Change role',
    verifyEmailTitle: 'Verify email manually',
    clickToVerify: 'Click to manually verify this email',
    verify: 'Verify',
    createUser: 'Create user',
    createUserTitle: 'Create user',
    create: 'Create',
    userCreated: 'User created',
    budget: 'Budget',
    budgetTitle: 'Monthly token budget',
    budgetPlaceholder: 'Token budget (empty = unlimited)',
    budgetUnlimited: 'Unlimited',
    usedThisMonth: 'Used this month',
    resetPasswordTitle: 'Reset password',
    newPassword: 'New password',
    you: 'you',
    roleAdmin: 'Admin',
    roleUser: 'User',
    roleViewer: 'Viewer',
    deactivateConfirm: 'Are you sure you want to deactivate "{username}" ({email})? They will be unable to log in.',
    activateConfirm: 'Re-activate "{username}" ({email})?',
    verifyEmailConfirm: 'Manually verify the email for "{username}" ({email})?',
  },

  // Welcome / Landing Page
  welcome: {
    subtitle: 'Intelligent Application Performance Monitoring powered by the Model Context Protocol. Predict issues, monitor metrics, and get AI-powered insights — all through a conversational interface.',
    getStarted: 'Get Started',
    featurePredictiveTitle: 'Predictive Analytics',
    featurePredictiveDesc: 'Forecast performance trends and detect anomalies before they impact your users using machine-learning models.',
    featureMetricsTitle: 'Metric Monitoring',
    featureMetricsDesc: 'Track CPU, memory, request latency and hundreds of infrastructure metrics in real time across all your hosts and services.',
    featureAlertsTitle: 'Alert Subscriptions',
    featureAlertsDesc: 'Define custom thresholds and receive timely notifications when your systems need attention.',
    featureChatTitle: 'AI-Powered Chat',
    featureChatDesc: 'Ask questions in natural language and get instant answers backed by live infrastructure data through MCP tools.',
    footer: 'Ключ-Астром MCP Platform',
  },

  // Command Palette
  commandPalette: {
    placeholder: 'Type a command… (e.g., /get_runtime_license)',
    noCommands: 'No matching commands',
  },

  // Modal
  modal: {
    close: 'Close modal',
    confirm: 'Confirm action',
    cancel: 'Cancel action',
  },

  // Tool Invocation Modal
  toolModal: {
    title: 'Execute Tool',
    serverLabel: 'Server',
    toolLabel: 'Tool',
    parametersLabel: 'Parameters',
    executeButton: 'Execute',
    executingButton: 'Executing...',
    closeButton: 'Close',
    resultLabel: 'Result',
    errorLabel: 'Error',
    noParametersMessage: 'This tool has no parameters',
    addToChatButton: 'Add to chat',
  },

  // Languages
  languages: {
    en: 'English',
    ru: 'Русский',
  },

  // Date/Time
  dateTime: {
    now: 'now',
    minutesAgo: (n: number) => `${n} minute${n === 1 ? '' : 's'} ago`,
    hoursAgo: (n: number) => `${n} hour${n === 1 ? '' : 's'} ago`,
    daysAgo: (n: number) => `${n} day${n === 1 ? '' : 's'} ago`,
    weeksAgo: (n: number) => `${n} week${n === 1 ? '' : 's'} ago`,
  },

  // Scheduled Queries
  scheduledQueries: {
    title: 'Scheduled Queries',
    description: 'Automate recurring queries on a schedule',
    create: 'Create',
    createQuery: 'Create Scheduled Query',
    editQuery: 'Edit Scheduled Query',
    confirmSaveTitle: 'Confirm scheduled query',
    confirmSaveMessage: 'Review this scheduled query before saving.',
    confirmSaveButton: 'Confirm & Save',
    name: 'Name',
    namePlaceholder: 'My daily report',
    queryText: 'Query Text',
    queryPlaceholder: 'Show me hosts with CPU usage > 80%',
    mode: 'Mode',
    modeQuery: 'Query',
    modeScenario: 'Scenario',
    scenario: 'Scenario',
    scenarioPlaceholder: 'Select a scenario',
    scenarioEmpty: 'No scenarios available',
    scenarioLoadError: 'Could not load scenarios',
    schedule: 'Schedule',
    customCron: 'Use custom cron expression',
    usePreset: 'Use preset',
    notification: 'Notification',
    notifyInApp: 'In-App',
    notifyTelegram: 'Telegram',
    lastRun: 'Last',
    nextRun: 'Next',
    runNow: 'Run Now',
    history: 'History',
    deactivate: 'Deactivate',
    activate: 'Activate',
    noQueries: 'No scheduled queries yet',
    noQueriesHint: 'Create your first scheduled query to automate recurring tasks.',
    noResult: 'No results yet',
    // In-chat scheduling
    scheduleThis: 'Schedule',
    scheduleThisTooltip: 'Schedule this prompt to run automatically',
    scheduleModalTitle: 'Schedule This Prompt',
    scheduleModalSubtitle: 'Run on a schedule with automatic notifications',
    scheduled: 'Scheduled!',
    scheduledHint: 'Your prompt will run on the selected schedule',
    editableInSettings: 'Editable in Settings → Scheduled Queries',
    willRun: 'Will run',
    advancedOptions: 'Advanced Options',
    queryTextEditable: 'Query Text (editable)',
    cronFormat: 'Format: min hour day month weekday',
    pickFromPresets: 'Pick from presets',
    useCustomCronExpression: 'Use custom cron expression',
    remove: 'Delete',
    deleteConfirmTitle: 'Delete Scheduled Query?',
    deleteConfirmHint: 'This will permanently remove the schedule and all its execution history. This cannot be undone.',
    deleting: 'Deleting...',
    historyOf: 'History',
    noHistory: 'No execution history yet.',
    telegramNotificationsEnabled: 'Telegram notifications enabled',
    statusSuccess: 'Success',
    statusError: 'Error',
    statusRunning: 'Running',
    cronPresets: {
      everyHour: 'Every hour',
      every6Hours: 'Every 6 hours',
      dailyAt9am: 'Daily at 9 AM',
      dailyAtMidnight: 'Daily at midnight',
      everyMondayAt9am: 'Every Monday at 9 AM',
      everyWeekdayAt9am: 'Every weekday at 9 AM',
      firstOfMonthAt9am: 'First of month at 9 AM',
    },
  },

  // Telegram
  telegram: {
    title: 'Telegram',
    description: 'Link your Telegram account to receive notifications and chat with the AI via Telegram',
    status: 'Connection Status',
    linked: 'Linked',
    notLinked: 'Not Linked',
    notLinkedHint: 'Generate a token to link your account',
    unlink: 'Unlink Account',
    generateToken: 'Generate Link Token',
    linkInstructions: 'Link Instructions',
    openInTelegram: 'Open in Telegram',
    howItWorks: 'How it works',
    feature1: 'Messages sent to the bot are processed as your authenticated queries',
    feature2: 'Scheduled query results can be sent to Telegram when notification is set to "Telegram"',
    feature3: 'Your account stays secure — the bot verifies your identity through the link token',
    botNotConfigured: 'Telegram Bot Not Configured',
    botNotConfiguredHint: 'Set the TELEGRAM_BOT_TOKEN environment variable to enable the Telegram bot.',
  },

  // Token Usage Statistics
  stats: {
    title: 'Token Usage Statistics',
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    allTime: 'All Time',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    last90Days: 'Last 90 days',
    requests: 'requests',
    requestsCount: { one: 'request', other: 'requests' },
    tokens: 'Tokens',
    cost: 'Cost',
    byProvider: 'Usage by Provider',
    byModel: 'Usage by Model',
    dailyBreakdown: 'Daily Breakdown',
    date: 'Date',
    provider: 'Provider',
    model: 'Model',
    promptTokens: 'Prompt',
    completionTokens: 'Completion',
    totalTokens: 'Total',
    noData: 'No usage data yet',
    noDataDescription: 'Token usage statistics will appear here as you use the chat.',
  },
  // Analytics
  analytics: {
    title: 'Analytics',
    sessionCost: 'Session Cost',
    toolCalls: 'Tool Calls',
    contextWindow: 'Context Window',
    performance: 'Performance',
    usageTrends: 'Usage Trends',
    allUsers: 'All users',
    scheduledSummary: 'Scheduled Summary',
    tokens: 'tokens',
    cost: 'Cost',
    requests: 'requests',
    totalCalls: 'Total Calls',
    uniqueTools: 'Unique Tools',
    blockedDuplicates: 'Blocked Duplicates',
    saved: 'saved',
    efficiency: 'Efficiency',
    topTools: 'Top Tools',
    callsPerDay: 'Calls per Day',
    contextUsage: 'Context Usage',
    turns: 'turns',
    entities: 'entities',
    avgResponseTime: 'Avg Response Time',
    p95Latency: 'P95 Latency',
    byModel: 'By Model',
    dailyTrend: 'Daily Trend',
    model: 'Model',
    avgMs: 'Avg ms',
    p95Ms: 'P95 ms',
    requestCount: 'Requests',
    activeSchedules: 'Active Schedules',
    runsThisWeek: 'Runs This Week',
    failures: 'Failures',
    successRate: 'Success Rate',
    totalRuns: 'Total Runs',
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    noData: 'No data',
    loading: 'Loading...',
    fetchError: 'Failed to load data',
    refresh: 'Refresh',
    collapse: 'Collapse',
    expand: 'Expand',
    viewAll: 'View All',
    recipeHitRate: 'Recipe Hit Rate',
    recipeName: 'Recipe',
    hitCount: 'Hits',
    avgDuration: 'Avg Duration',
    lastFired: 'Last Fired',
    noRecipeHits: 'No recipe hits recorded yet',
    providerVolume: 'Provider Volume',
    noQueriesLogged: 'No queries logged yet',
    queries: 'queries',
    toolFrequency: 'Tool Frequency',
    noToolCalls: 'No tool calls recorded yet',
    callCount: 'Calls',
    blockRate: 'Block Rate',
    days7: '7 days',
    days30: '30 days',
    ms: 'ms',
    seconds: 's',
    daysShort: 'd',
    reqShort: 'req',
    avgAbbrev: 'avg',
    // STORY-289: counted nouns, picked with Intl.PluralRules (i18n/plural.ts)
    counts: {
      tokens: { one: 'token', other: 'tokens' },
      toolCalls: { one: 'total call', other: 'total calls' },
      uniqueTools: { one: 'unique tool', other: 'unique tools' },
      recipes: { one: 'recipe', other: 'recipes' },
      hits: { one: 'hit', other: 'hits' },
      queries: { one: 'query', other: 'queries' },
      providers: { one: 'provider', other: 'providers' },
      blockedDuplicates: { one: 'blocked duplicate', other: 'blocked duplicates' },
      calls: { one: 'call', other: 'calls' },
      schedules: { one: 'schedule', other: 'schedules' },
      active: { one: 'active', other: 'active' },
      activeSchedules: { one: 'active schedule', other: 'active schedules' },
      runsThisWeek: { one: 'run this week', other: 'runs this week' },
      failures: { one: 'failure', other: 'failures' },
      totalRuns: { one: 'total run', other: 'total runs' },
      turns: { one: 'turn', other: 'turns' },
      hiddenDuplicates: { one: 'duplicate hidden', other: 'duplicates hidden' },
      times: { one: 'time', other: 'times' },
      failedClusters: { one: 'cluster failed', other: 'clusters failed' },
      splitQueries: { one: 'split query', other: 'split queries' },
      parts: { one: 'part', other: 'parts' },
    },
    topPrefix: 'Top',
    requestTimedOut: 'The request timed out',
    // STORY-301: admin Compound Statistics panel
    compound: {
      title: 'Compound Query Statistics',
      adminOnly: 'Admin',
      period: 'Period',
      days1: '1 day',
      days7: '7 days',
      days30: '30 days',
      days90: '90 days',
      empty: 'No compound queries in this period.',
      classifierOff: 'The compound classifier is off, so there is nothing to measure. Turn on COMPOUND_CLASSIFIER_ENABLED to collect these statistics.',
      sectionTypes: 'Query types',
      // Round 4: the class table covers only the turns the LLM classified;
      // the coverage line accounts for every other turn in the header total.
      classifiedByLlm: 'Classified by the LLM',
      coverage: 'How the classifier handled these queries',
      coverageTotal: 'All queries',
      coverageScreened: 'Screened by the cue filter, no LLM call',
      coverageLlm: 'Classified by the LLM',
      coverageShadow: 'Of them in shadow mode (run as one query)',
      coverageOff: 'Classifier off',
      coverageError: 'Classifier error (run as one query)',
      coverageUnrecorded: 'Status not recorded',
      coverageNote: 'Executed plan counts every query the classifier handled, with or without an LLM call: queries screened by the cue filter run as Single (or as Sequential steps when the built-in follow-up rules plan them) and are timed as Simple below. Queries with the classifier off have no plan and are not in these tables.',
      executedPlan: 'Executed plan',
      // STORY-297: who planned an executed sequential split
      plannedBy: 'Sequential steps planned by',
      planSources: {
        classifier: 'LLM classifier',
        tier1: 'Built-in follow-up rules (no LLM)',
        other: 'Other',
      },
      sectionFailures: 'Part failures',
      noSplits: 'No query was split into parts in this period.',
      noFailures: 'No part failed or was skipped in this period.',
      failRate: 'Failure rate',
      failed: 'Failed',
      skipped: 'Skipped',
      skippedHint: 'Skipped parts never ran (a follow-up step whose first step failed or found nothing) and are not counted as failures.',
      byReason: 'By reason',
      byPosition: 'By part',
      byDomain: 'By domain',
      recentFailures: 'Recent failed parts',
      legacyHint: 'Parts recorded before this version have no reason.',
      sectionLatency: 'Added latency',
      baselineSimple: 'Compared with single queries in the same period',
      baselineRecipe: 'Compared with recipe answers that were not classified',
      noBaseline: 'No single-query baseline in this period, so the added latency cannot be computed.',
      colLabel: 'Name',
      colCount: 'Count',
      colShare: 'Share',
      colTime: 'Time',
      colPlan: 'Plan',
      colPart: 'Part',
      colDomain: 'Domain',
      colReason: 'Reason',
      colDuration: 'Duration',
      colP50: 'p50',
      colP95: 'p95',
      colExtraP50: 'Added p50',
      colExtraP95: 'Added p95',
      byClass: 'By class',
      byPlan: 'By plan',
      partPrefix: 'Part',
      otherLabel: 'Other',
      statusFail: 'failed',
      statusSkipped: 'skipped',
      classes: {
        simple: 'Simple',
        parallel: 'Parallel',
        sequential: 'Sequential',
        compare: 'Comparison',
        ambiguous: 'Ambiguous',
        other: 'Other',
      },
      plans: {
        single: 'Single',
        parallel_n: 'Parallel parts',
        sequential: 'Sequential steps',
        compare: 'Comparison',
        other: 'Other',
      },
      reasons: {
        timeout: 'Timed out',
        cancelled: 'Cancelled',
        exception: 'Failed with an error',
        no_result: 'Returned no result',
        budget_blocked: 'Token budget exhausted',
        provider_error: 'LLM provider error',
        content_policy: 'Refused by content policy',
        confidential_refusal: 'Refused: confidential data',
        providers_unavailable: 'All LLM providers unavailable',
        context_overflow: 'Context too large',
        validation_error: 'Validation error',
        error_path: 'Error inside the part (see logs)',
        unexpected_path: 'Unexpected answer path',
        upstream_failed: 'Skipped: previous step failed',
        no_followup_refs: 'Skipped: nothing to follow up',
        unrecorded: 'Reason not recorded (before this version)',
        other: 'Other',
      },
      domains: {
        problems: 'Problems',
        entities: 'Entities',
        metrics: 'Metrics',
        logs: 'Logs',
        k8s: 'Kubernetes',
        dashboards: 'Dashboards',
        rca: 'Root-cause analysis',
        unknown: 'Unknown',
      },
    },
  },

  // Configuration wizard (first-run setup)
  configuration: {
    title: 'System Configuration',
    subtitle: 'To get started, configure your cluster connection and LLM provider.',
    clusterSection: 'Cluster Connection',
    llmSection: 'LLM Provider',
    clusterUrl: 'Cluster Base URL',
    clusterUrlPlaceholder: 'https://your-cluster.example.com/e/environment-id',
    clusterToken: 'Cluster API Token',
    clusterTokenPlaceholder: 'dt0c01.XXXXXXXX.XXXXXXXX',
    envToken: 'Environment API Token',
    envTokenPlaceholder: 'dt0c01.XXXXXXXX.XXXXXXXX',
    gigachatCredentials: 'GigaChat Credentials (Base64)',
    gigachatPlaceholder: 'YzI4YjY4ZGUtNDk4OS00NDEy...',
    ollamaUrl: 'Ollama Base URL',
    ollamaPlaceholder: 'http://localhost:11434',
    testConnection: 'Test',
    saveAndApply: 'Save & Apply',
    saveAndContinue: 'Save & Continue',
    continueToLlm: 'Continue to LLM Setup',
    backToCluster: 'Back',
    finishSetup: 'Finish Setup',
    saving: 'Saving...',
    testing: 'Testing...',
    testSuccess: 'Connection successful',
    testFailed: 'Connection failed',
    saveSuccess: 'Configuration saved! Reloading...',
    saveFailed: 'Failed to save configuration',
    configured: 'Configured',
    notConfigured: 'Not configured',
    alreadyConfigured: 'Cluster connection is already configured. You can update the values below or continue to LLM setup.',
    optional: 'Optional',
    atLeastOneLlm: 'Add at least one LLM provider to enable the chat assistant.',
    finishBlockedLlm: 'No active LLM provider detected yet. Activate a provider above, then try again.',
    finishBlockedCluster: 'LLM provider is ready, but the cluster connection is not configured. Go back to step 1 and set the cluster URL and tokens.',
    maskPlaceholder: '****',
    step1Label: '1. Cluster',
    step2Label: '2. LLM Provider',
    clusterAlreadyConfigured: 'Cluster credentials are already configured via MCP server. You can skip this step or enter new values to override.',
    // Shown to non-admins instead of the setup page (only admins can configure).
    notConfiguredTitle: 'The system is not configured yet',
    notConfiguredBody: 'The cluster connection or an LLM provider has not been set up, so chat answers may be unavailable.',
    notConfiguredContactAdmin: 'Contact an administrator to finish the setup.',
  },
  clusterMode: {
    pill: 'Clusters',
    single: 'One cluster',
    singleDesc: 'Fastest and cheapest. Query a single cluster.',
    sequential: 'All — one at a time',
    sequentialDesc: 'Query each cluster in turn. Same cost per query times the number of clusters.',
    sequentialShort: 'sequential',
    parallel: 'All — in parallel',
    parallelDesc: 'Query all clusters at once. Roughly the latency of a single query.',
    parallelShort: 'parallel',
    selectClusters: 'Select clusters:',
    activeCluster: 'Active cluster:',
    defaultClusterShort: 'default',
    noActiveClusterHint: 'No cluster selected — the default cluster will be used.',
    synthesis: 'Merge answers with a final summary',
    synthesisOnDesc: 'One extra LLM call to merge cluster answers into one coherent response.',
    synthesisOffDesc: "Show each cluster's answer separately (faster, no extra LLM call).",
    selectionUpdated: 'Cluster selection updated — no longer available: ',
    // STORY-300: @-mention a cluster in the message
    mentionListLabel: 'Clusters to mention',
    mentionChip: 'Cluster',
    mentionChipTitle: 'Answered on the cluster named with @ in the question',
  },

  pagination: {
    expired: 'Results expired',
    expiredHint: 'Please re-run your query to get fresh results.',
  },

  alerts: {
    title: 'Alert Rules',
    newRule: 'New Rule',
    editRule: 'Edit Alert Rule',
    deleteConfirm: 'Delete this alert rule and all its events?',
    noRules: 'No alert rules yet',
    noRulesHint: 'Create your first alert rule to start monitoring.',
    noEvents: 'No alert events recorded yet.',
    firing: 'Firing',
    resolved: 'Resolved',
    rules: 'Rules',
    events: 'Events',
    severity: 'Severity',
    condition: 'Condition',
    threshold: 'Threshold',
    interval: 'Evaluation Interval',
    channels: 'Notification Channels',
    metricQuery: 'Metric Query',
    ruleKind: 'Kind',
    kindMetric: 'Metric change',
    kindSlo: 'SLO violation',
    service: 'Service',
    servicePlaceholder: 'ExampleService',
    serviceHint: 'Empty — all SLOs',
    serviceAll: 'All SLOs',
    entitySelector: 'Entity Selector',
    description: 'Description',
    lastEvaluated: 'Last Evaluated',
    enabled: 'Enabled',
    disabled: 'Disabled',
    enabledOfTotal: '{enabled} enabled / {total}',
    totalRules: { one: 'total rule', other: 'total rules' },
    firingCount: { one: 'firing', other: 'firing' },
    critical: 'Critical',
    warning: 'Warning',
    info: 'Info',
    // form fields
    name: 'Name',
    webhookUrl: 'Webhook URL',
    save: 'Save Rule',
    saving: 'Saving...',
    cancel: 'Cancel',
    validationRequired: 'Name, metric query, and threshold are required.',
    validationNameRequired: 'Name is required.',
    saveFailed: 'Failed to save alert rule',
    namePlaceholder: 'CPU Alert',
    sloNamePlaceholder: 'SLO violation',
    metricQueryPlaceholder: 'builtin:host.cpu.usage — or a question: What is the highest CPU usage on any host?',
    thresholdPlaceholder: '80',
    webhookPlaceholder: 'https://example.com/webhook',
    descriptionPlaceholder: 'Optional description',
    entitySelectorPlaceholder: 'type("HOST")',
    // condition operator labels
    condGt: '> (greater than)',
    condLt: '< (less than)',
    condGte: '>= (greater or equal)',
    condLte: '<= (less or equal)',
    condEq: '== (equal)',
    condNeq: '!= (not equal)',
    // notification channel labels
    chInApp: 'In-App',
    chTelegram: 'Telegram',
    chEmail: 'Email',
    chWebhook: 'Webhook',
    // evaluation interval labels
    int30s: '30 seconds',
    int1m: '1 minute',
    int5m: '5 minutes',
    int10m: '10 minutes',
    int30m: '30 minutes',
    int1h: '1 hour',
    // write confirmation (STORY-155, human-in-the-loop)
    confirmSaveTitle: 'Confirm alert rule change',
    confirmSaveMessage: 'Review this change before it is saved.',
    confirmSaveButton: 'Confirm & Save',
    confirmDeleteTitle: 'Confirm deletion',
    confirmDeleteButton: 'Confirm & Delete',
    back: 'Back',
    // rule details, events, icon-only controls (F-3)
    enableRule: 'Enable rule',
    disableRule: 'Disable rule',
    deleteRule: 'Delete rule',
    showEvents: 'Show rule events',
    hideEvents: 'Hide rule events',
    intervalEvery: 'Interval: every {seconds}s',
    recentEvents: 'Recent events',
    // STORY-275: an alert rule the chat proposed — saved only from the card
    proposalTitle: 'Proposed alert rule',
    proposalHint: 'The rule is not created yet. It is saved under your account only after you confirm.',
    proposalCreate: 'Create rule…',
    proposalConfirmTitle: 'Create this alert rule?',
    proposalConfirmMessage: 'Exactly this rule will be saved. Cancelling writes nothing.',
    proposalCreated: 'Rule "{name}" created — it is on the Alert rules page.',
    proposalAlreadyCreated: 'This rule already exists: "{name}" — it is on the Alert rules page. It is not created again.',
    proposalDefault: 'default',
    proposalAllHosts: 'all hosts (no filter)',
    proposalAllSlos: 'all SLOs (no filter)',
    proposalEvery: 'every {seconds} s',
    resolvedAt: 'resolved',
    // event rows formatted from condition/threshold (language-switchable)
    eventFiringMsg: '{name}: {value} {condition} {threshold}',
    eventResolvedMsg: '{name}: resolved ({value} no longer {condition} {threshold})',
    ambiguousResponse: 'Ambiguous response — no comparison',
    skipSloRecipe: 'SLO recipe could not be read',
    skipSloList: 'Answer is not a list of SLOs',
    skipSlo: 'SLO check was skipped',
  },
  hitl: {
    title: 'Writes awaiting your approval',
    approve: 'Approve',
    deny: 'Deny',
    expiresIn: 'expires in',
    argsUnavailable: 'args not held here — re-run the query',
    approvedResult: 'Approved and executed:',
    deniedResult: 'Denied — nothing was written:',
    dismiss: 'Dismiss',
  },
  promptHints: {
    title: 'Try one of these',
    examples: [
      'Show all hosts',
      'What problems are open right now?',
      'Show CPU and memory for the busiest hosts',
      'Which services have the most errors in the last hour?',
    ],
  },
  dispatch: {
    title: 'Prompt Customization',
    subtitle: 'Add your own phrasings, descriptions and hints so the assistant picks the right tool. Built-in rules are never replaced — only extended.',
    scope: 'Scope',
    scopeRecipePattern: 'Recipe phrasing (regex)',
    scopeRecipeDescription: 'Recipe description (for the classifier)',
    scopeSpecialistAddendum: 'Specialist prompt addendum',
    target: 'Target',
    targetRecipe: 'Recipe',
    targetSpecialist: 'Specialist',
    language: 'Language',
    payload: 'Value',
    payloadPatternHint: 'Regular expression (same as the built-in recipes)',
    payloadDescriptionHint: 'Description text the tier-2 classifier reads',
    payloadAddendumHint: 'Text appended to the end of the specialist system prompt',
    enabled: 'Enabled',
    add: 'Add',
    create: 'Save override',
    delete: 'Delete',
    confirmDelete: 'Delete this override?',
    existingOverrides: 'Active overrides',
    noOverrides: 'No overrides yet',
    custom: 'Custom',
    builtin: 'Built-in',
    loadFailed: 'Failed to load overrides',
    actionFailed: 'Action failed',
    created: 'Override saved',
    deleted: 'Override deleted',
    testerTitle: 'Dispatch tester',
    testerSubtitle: 'Type a query and see which recipe handles it — before and after a draft override.',
    testQuery: 'Test query',
    testQueryPlaceholder: 'e.g. top 5 hosts by CPU',
    runTest: 'Test',
    before: 'Now',
    after: 'After change',
    matchedRecipe: 'Matched recipe',
    noMatch: 'No match (query would go to the LLM classifier / agentic path)',
    matchedPattern: 'Matched phrasing',
    specificity: 'Specificity',
    tier: 'Tier',
    blockedByGuard: 'Blocked by a guard on recipes',
    validationValid: 'Validation passed — safe to save',
    validationInvalid: 'Rejected by validation',
    reasonRegexError: 'Invalid regular expression',
    reasonTooBroad: 'Pattern is too broad',
    reasonMatchesEmpty: 'Pattern matches the empty string (grabs everything)',
    reasonSecret: 'A secret / sensitive value was detected',
    reasonTooLong: 'Value is too long',
    reasonUnknownTarget: 'Unknown target',
    reasonBadLanguage: 'Invalid language',
    reasonSteals: 'Pattern would steal an already-working query',
    draftHint: 'The draft is validated automatically as you type the value below.',
    descTitle: 'MCP tool descriptions',
    descSubtitle: "Nudge the LLM's tool choice in agentic mode by appending to or replacing a tool's description. Descriptive-only — parameters and schemas are never changed. Saved to the MCP host and applied live, no restart.",
    descNone: 'No description overrides yet.',
    descToolName: 'tool name',
    descTextHint: 'description text to append / replace',
    descAppend: 'append',
    descReplace: 'replace',
    descMultilingual: 'multilingual entry — edit in the file',
    descSave: 'Save & apply',
    descSaved: 'Saved & applied',
    descReload: 'Reload from disk',
    descReloaded: 'Reloaded from disk',
  },
  // STORY-298: the interactive chart under a chart answer
  chart: {
    region: 'Interactive chart',
    legend: 'Chart series',
    toggleSeries: 'Show or hide the series “{label}”',
    lastSeries: 'The last visible series cannot be hidden',
    plotHint: 'Left and right arrows move between points, up and down between series, “+” zooms in, “0” resets the zoom. With a mouse: drag across a range to zoom in.',
    resetZoom: 'Reset zoom',
    savePng: 'Save as PNG',
    saving: 'Saving…',
    savedSvg: 'The browser refused a PNG — the chart was saved as SVG',
    saveFailed: 'Could not save the chart',
    period: 'Period: {period}',
    zoomed: 'Zoomed in: {from} – {to}',
    zoomedOf: 'Zoomed in: {from} – {to} (full period: {period})',
    truncated: 'showing {shown} of {total}',
    fullListInText: 'All {total} are listed in the answer text above.',
    downsampled: 'points averaged: {from} → {to}',
    countUnits: {
      hosts: { one: 'host', other: 'hosts' },
      points: { one: 'point', other: 'points' },
    },
    bucket: 'bucket',
    noValue: 'no data',
    moreSeries: '+{n} more',
    point: 'point {n}',
  },
  // STORY-304: the actions under an incident card
  incidentCard: {
    actions: 'Incident card actions',
    download: 'Download .md',
    downloadLabel: 'Download the incident card as a Markdown file',
    downloaded: 'Card saved: {name}',
    print: 'Print / PDF',
    printLabel: 'Print the incident card or save it as PDF',
    sendTelegram: 'Send to Telegram',
    sendTelegramLabel: 'Send the incident card to your linked Telegram chat',
    sending: 'Sending…',
    sent: 'The card was sent to Telegram',
    sendFailed: 'The card was not sent: {message}',
  },
  // STORY-303: user knowledge bases
  knowledge: {
    title: 'Knowledge base',
    subtitle: 'Documents the assistant draws on when it answers. Matching excerpts are given to the model with the question, and the source is named under the answer.',
    listTitle: 'Documents',
    empty: 'No documents yet.',
    loading: 'Loading…',
    colTitle: 'Title',
    colScope: 'Access',
    colCluster: 'Cluster',
    colChunks: 'Excerpts',
    colDate: 'Added',
    colActions: 'Actions',
    allClusters: 'all clusters',
    scopeShared: 'Shared',
    scopePersonal: 'Personal',
    scopeLabel: 'Who can see the document',
    scopeSharedHint: 'Shared — visible to every user',
    scopePersonalHint: 'Personal — visible only to you',
    personalOnlyNote: 'Your documents are visible only to you.',
    uploadTitle: 'Add a document',
    titleLabel: 'Document title',
    sourceMode: 'How to add it',
    modePaste: 'Paste text',
    modeFile: 'Upload a file',
    contentLabel: 'Document text',
    contentPlaceholder: 'Paste a runbook, a procedure or a note…',
    fileLabel: 'File (.txt, .md, .log)',
    fileChosen: 'Selected file: {name}',
    fileUnsupported: 'Only .txt, .md and .log text files are supported.',
    fileTooLarge: 'The file is too large: {limit} KB at most.',
    upload: 'Add',
    uploading: 'Adding…',
    uploaded: 'Document “{title}” added: {chunks} excerpt(s).',
    redactedNotice: 'Personal data was masked before saving: {kinds}.',
    blocked: 'The document was not saved: it contains {kinds}. Remove it and try again.',
    deleteLabel: 'Delete document “{title}”',
    deleteTitle: 'Delete the document?',
    deleteConfirm: 'Document “{title}” and all its excerpts will be deleted permanently.',
    deleted: 'Document deleted.',
    searchTitle: 'Try the search',
    searchHint: 'Shows the excerpts the model would be given for this question.',
    searchLabel: 'Question',
    searchButton: 'Search',
    searchNone: 'No matching excerpts — the answer will be given without the knowledge base.',
    error: 'The action failed: {message}',
    kinds: {
      secret: 'a secret (token, key or password)',
      email: 'an email address',
      card: 'a card number',
      ssn: 'a social security number',
      iban: 'an IBAN account number',
    },
  },
};
